// ─── Shared trade-in image helpers (ERP-5022) ─────────────────────────────────
// Step 1's Listing Image and Step 2's Listing Item images use the same picker over one
// listing-wide upload pool, so everything both steps need to agree on lives here: the
// device defaults, the pool's id/source bookkeeping, and the upload validation rules.
//
// These were Step 2's private helpers until Step 1 needed identical behaviour. Moved out
// rather than copied so the two levels can't drift — a difference between them is what
// makes an image resolve to the wrong file.

import noImage from '../../assets/img/brand/noImage.png';
import tradeinPhoneDefault from '../../assets/img/trade-in-listing/Mister-Mobile-Placeholder-1.png';
import tradeinTabletDefault from '../../assets/img/trade-in-listing/mister-mobile-tablet.png';
import tradeinWatchDefault from '../../assets/img/trade-in-listing/Mister-Mobile-Watch.png';
import tradeinAccessoryDefault from '../../assets/img/trade-in-listing/Apple-Earbuds-Placeholder-1.png';
import apiHelper from '../../apiHelper.js';

export const YELLOW = '#FFEB00';

// Per-device default image (ERP-5022). A listing item — and the listing itself — defaults to
// the brand placeholder for the Step-1 Listing Device instead of the SPU's first image, and
// that image is shown as a non-deletable default tile in the Upload tab.
export const DEVICE_DEFAULT_IMAGES = {
    phone: tradeinPhoneDefault,
    tablet: tradeinTabletDefault,
    watch: tradeinWatchDefault,
    accessories: tradeinAccessoryDefault,
};

// Phone, not noImage, is the fallback. Step 1 can have an SPU picked before the Listing
// Device — the two fields are independent and the image field renders as soon as the SPU
// lands — so the Listing Image has to show something real in the meantime, and a generic
// "no image" placeholder reads as an error rather than a default. Step 2 never reaches the
// fallback: Step 1 won't advance without a device.
export const getDeviceDefaultImage = (device) =>
    DEVICE_DEFAULT_IMAGES[(device || '').toLowerCase()] || DEVICE_DEFAULT_IMAGES.phone;

// A device-default is a static bundled asset, never a real user upload — if its URL ever shows
// up as a saved active image or inside upload history (e.g. a snapshot taken while a stale
// device was selected), it must not be treated as a real image: it should neither pin as a
// fake "current image" tile nor appear as a deletable history entry.
export const DEVICE_DEFAULT_URLS = new Set(Object.values(DEVICE_DEFAULT_IMAGES));
export const isDeviceDefaultUrl = (url) => Boolean(url) && DEVICE_DEFAULT_URLS.has(url);

// Re-point a row/listing showing a device default at the CURRENT Listing Device's placeholder.
// Anything holding a real chosen image is returned untouched. Recognised by the isDeviceDefault
// flag, with a URL check as backup for state saved before the flag existed.
//
// Needed because the resolved asset URL is what gets stored: something created under Phone
// still carries the Phone placeholder after the user goes back to Step 1 and switches to Watch.
export const withCurrentDeviceDefault = (image, device) => {
    const url = getDeviceDefaultImage(device);
    const isDefault = image?.isDeviceDefault || isDeviceDefaultUrl(image?.url);
    if (!isDefault || image?.url === url) return image;
    return { id: null, url, isPlaceholder: true, isDeviceDefault: true };
};

// Best-effort display name for an image whose real filename isn't available (the backend
// re-snapshots the active image on every save, so it never round-trips a stored filename) —
// falls back to the last path segment of its URL rather than a vague generic label.
export const filenameFromUrl = (url) => {
    if (!url) return null;
    try {
        const clean = url.split('?')[0].split('#')[0];
        const last = clean.substring(clean.lastIndexOf('/') + 1);
        return last ? decodeURIComponent(last) : null;
    } catch {
        return null;
    }
};

// Exact match only — these URLs can come from a generic image-serving endpoint where the query
// string is the ONLY thing distinguishing one file from another (e.g. `/media/serve?key=...`).
// Stripping it before comparing (an earlier version of this) made unrelated images look like
// the same one, which misclassified a selected upload as an SPU-tab image.
export const sameImageUrl = (a, b) => Boolean(a) && Boolean(b) && a === b;

// ─── Pool sources ─────────────────────────────────────────────────────────────
// Which table a pool tile's id lives in. tradein_listing_images and
// tradein_listing_item_images have independent primary-key sequences, so with both levels
// sharing one Upload tab an id alone no longer identifies an image — pk 5 exists in both and
// is a different picture in each. Every saved tile therefore carries the source it came from,
// and that source picks the `*_image_tab` the backend resolves it against (UPLOAD_TAB_MODELS).
export const SOURCE_LISTING = 'listing';
export const SOURCE_ITEM = 'item';

// The tab value the backend expects for a pool entry from this source. A tile with no source
// predates the shared pool and can only have come from an item.
export const tabForSource = (source) => (source === SOURCE_LISTING ? 'listing' : 'tradein');

// Both levels mint `upload-N` ids into the SAME pool, so each has to seed its counter past
// everything already there. Without this Step 2 restarts at 0, hands out an id Step 1 already
// used, and the two different files are then treated as one image everywhere ids are compared.
export const maxUploadIndex = (entries) => {
    let max = 0;
    (entries || []).forEach(e => {
        const m = typeof e?.id === 'string' && e.id.match(/^upload-(\d+)$/);
        if (m) max = Math.max(max, parseInt(m[1], 10));
    });
    return max;
};

// One tile per photo, keyed on the original filename rather than the URL: every row that uses
// a file keeps its own independent copy of it, so the same photo legitimately arrives under
// different ids AND different URLs, and URL-matching would show it once per row. Filenames are
// unique within a listing (duplicates are rejected at upload), so they're the reliable
// identity; the id is only a fallback for an entry with no name yet.
export const dedupePoolEntries = (entries) => {
    const seenIds = new Set();
    const seenNames = new Set();
    const out = [];
    (entries || []).forEach(e => {
        if (!e) return;
        if (seenIds.has(e.id) || (e.name && seenNames.has(e.name))) return;
        seenIds.add(e.id);
        if (e.name) seenNames.add(e.name);
        out.push(e);
    });
    return out;
};

// True when `image` is one of the pictures in `removed` ([{id, source, url, name}]).
//
// Matched on any of the three, because "the same picture" doesn't mean "the same row": the
// listing and each item keep their own independent copy of a file they use, so one photo
// exists under several ids and often several URLs. Removing a tile has to re-point every
// one of them, which the id alone can't find. The backend does the authoritative version of
// this on save (_purge_removed_uploads); this is what keeps the thumbnails honest before it.
export const isRemovedImage = (image, removed) => {
    if (!image || !(removed || []).length) return false;
    return removed.some(r =>
        (image.id != null && r.id === image.id) ||
        (r.url && image.url && r.url === image.url) ||
        (r.name && image.name && r.name === image.name)
    );
};

// ─── Upload validation ────────────────────────────────────────────────────────
// Accepted upload formats + size limit for the "Upload" tab
export const UPLOAD_ACCEPTED_TYPES = ['image/png', 'image/webp', 'image/jpeg'];
export const UPLOAD_MAX_SIZE = 1 * 1024 * 1024; // 1MB
// Wording is fixed by the spec — kept verbatim, including "are support", and matching the
// Upload tab's side note. Don't "correct" the grammar.
export const UPLOAD_ERROR_MESSAGE = 'Only PNG/WEBP/JPEG files below 1MB are support';
export const UPLOAD_DUPLICATE_MESSAGE = 'Duplicate filenames cannot be uploaded';

// Names already taken by a DIFFERENT trade-in listing. The Upload tab can only compare
// against the listing being edited — the pool never sees another one — but trade-in images
// share a single flat folder on the server, so a name used anywhere else is a real clash:
// same bytes are deduped onto one file, different bytes get a random suffix that leaks into
// the WooCommerce 'Model Image' and the pricing Sheet (ERP-5022).
//
// Fails open. A check that can't complete lets the upload through rather than blocking the
// user on a network hiccup — the backend still stores the file safely, it just renames it,
// which is exactly the behaviour we had before this check existed.
export async function fetchTakenFilenames(names, excludeListingId) {
    if (!names.length) return new Set();
    try {
        const res = await apiHelper('sales/trade-in-listings/check-image-names/', 'POST', {
            filenames: names,
            exclude_listing_id: excludeListingId || null,
        });
        if (!res?.ok) return new Set();
        const data = await res.json();
        return new Set(Array.isArray(data?.taken) ? data.taken : []);
    } catch (err) {
        console.error('Image name check failed:', err);
        return new Set();
    }
}

export { noImage };
